import React, { useState, useEffect } from 'react';
import {
  Card, CardBody, Col, Container, Row, Input, Label, Form, FormFeedback,
  Button, Alert, Progress,
} from 'reactstrap';
import BreadCrumb from '../Components/Common/BreadCrumb';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import Dropzone from 'react-dropzone';
import { CSSTransition } from 'react-transition-group';
import { createStaticContent, updateStaticContent, getStaticContent } from '../services/api';

const StaticContentForm = () => {
  document.title = 'مدیریت محتوای صفحه اصلی | فات 90';

  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [contentId, setContentId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);
  const [showDropzone, setShowDropzone] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState({
    WhatIsFut90: '',
    VideoFile: null,
  });

  const validationShape = Yup.object().shape({
    WhatIsFut90: Yup.string()
      .required('لطفاً توضیحات فات 90 چیست؟ را وارد کنید')
      .max(800, 'توضیحات باید حداکثر 800 کاراکتر باشد'),
    ...(isEditMode ? {} : {
      VideoFile: Yup.mixed()
        .required('لطفاً یک فایل ویدیویی انتخاب کنید')
        .test('fileType', 'فقط فایل‌های mp4 یا webm مجاز هستند', (value) =>
          value ? ['video/mp4', 'video/webm'].includes(value.type) : true
        )
        .test('fileSize', 'حجم فایل باید کمتر از 50MB باشد', (value) =>
          value ? value.size <= 50 * 1024 * 1024 : true
        )
    })
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema: validationShape,
    onSubmit: async (values) => {
      setError(null);
      setSuccess(null);
      try {
        const data = {
          WhatIsFut90: values.WhatIsFut90,
          VideoFile: values.VideoFile,
        };
        if (isEditMode && !values.VideoFile) delete data.VideoFile;

        let response;
        if (isEditMode) {
          response = await updateStaticContent(contentId, data, {
            onUploadProgress: (e) => {
              const percent = Math.round((e.loaded * 100) / e.total);
              setUploadProgress(percent);
            },
          });
          setSuccess('محتوا با موفقیت بروزرسانی شد.');
          setCurrentVideoUrl(response.data.videoUrl);
          setShowDropzone(false);
        } else {
          response = await createStaticContent(data, {
            onUploadProgress: (e) => {
              const percent = Math.round((e.loaded * 100) / e.total);
              setUploadProgress(percent);
            },
          });
          setSuccess('محتوا با موفقیت ثبت شد.');
          setIsEditMode(true);
          setContentId(response.data.id);
          setCurrentVideoUrl(response.data.videoUrl);
        }

        setSelectedFile(null);
        validation.setFieldValue('VideoFile', null);
        validation.setTouched({ ...validation.touched, VideoFile: false });
        validation.setErrors({ ...validation.errors, VideoFile: undefined });
        setTimeout(() => navigate('/dashboard'), 2000);
      } catch (err) {
        let errorMessage = 'خطایی رخ داد';
        if (err.response?.status === 400) {
          errorMessage = err.response.data.message?.toLowerCase().includes('videofile')
            ? 'لطفاً یک فایل ویدیویی معتبر انتخاب کنید'
            : err.response.data.message || 'ورودی‌ها نامعتبر هستند';
        } else if (err.response?.status === 401) {
          errorMessage = 'لطفاً دوباره وارد سیستم شوید';
        } else if (err.response?.status === 404) {
          errorMessage = 'محتوا یافت نشد';
        }
        setError(errorMessage);
        setUploadProgress(0);
      }
    },
  });

  const fetchContent = async () => {
    try {
      const response = await getStaticContent();
      setFormInitialValues({
        WhatIsFut90: response.data.whatIsFut90 || '',
        VideoFile: null,
      });
      setIsEditMode(true);
      setContentId(response.data.id);
      setCurrentVideoUrl(response.data.videoUrl);
    } catch (err) {
      if (err.response?.status === 404) {
        setIsEditMode(false);
        setContentId(null);
        setCurrentVideoUrl(null);
        setFormInitialValues({ WhatIsFut90: '', VideoFile: null });
      } else if (err.response?.status === 401) {
        setError('لطفاً دوباره وارد سیستم شوید');
      } else {
        setError('خطا در بارگذاری محتوا');
      }
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleDrop = async (acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      await validation.setFieldValue('VideoFile', file, true);
      validation.setTouched({ ...validation.touched, VideoFile: true });
      validation.setErrors({ ...validation.errors, VideoFile: undefined });
    }

    if (rejectedFiles.length > 0) {
      setSelectedFile(null);
      await validation.setFieldValue('VideoFile', null, true);
      validation.setTouched({ ...validation.touched, VideoFile: true });

      const rejectedFile = rejectedFiles[0];
      const errorCodes = rejectedFile.errors.map((err) => err.code);

      if (errorCodes.includes('file-too-large')) {
        validation.setErrors({ ...validation.errors, VideoFile: 'حجم فایل باید کمتر از 50MB باشد' });
      } else if (errorCodes.includes('file-invalid-type')) {
        validation.setErrors({ ...validation.errors, VideoFile: 'فقط فایل‌های mp4 یا webm مجاز هستند' });
      } else {
        validation.setErrors({ ...validation.errors, VideoFile: 'فایل نامعتبر است' });
      }
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="مدیریت محتوای صفحه اصلی" pageTitle="مدیریت" />
        <Row>
          <Col lg={8}>
            <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); return false; }}>
              <Card>
                <CardBody>
                  {error && <Alert color="danger">{error}</Alert>}
                  {success && <Alert color="success">{success}</Alert>}

                  <div className="mb-3">
                    <Label htmlFor="what-is-fut90-input">توضیحات فات 90 چیست؟</Label>
                    <Input
                      type="textarea"
                      id="what-is-fut90-input"
                      name="WhatIsFut90"
                      rows="5"
                      value={validation.values.WhatIsFut90}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.WhatIsFut90 && !!validation.errors.WhatIsFut90}
                    />
                    <FormFeedback>{validation.errors.WhatIsFut90}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label>فایل ویدیویی</Label>
                    {isEditMode && currentVideoUrl && !showDropzone && (
                      <>
                        <video
                          src={`${process.env.REACT_APP_API_BASE_URL}${currentVideoUrl}`}
                          controls
                          className="w-100"
                          style={{ maxHeight: '200px' }}
                        />
                        <Button
                          color="secondary"
                          size="sm"
                          className="mt-2"
                          onClick={() => setShowDropzone(true)}
                        >
                          تغییر ویدیو
                        </Button>
                      </>
                    )}

                    <CSSTransition in={showDropzone || !isEditMode} timeout={300} classNames="fade" unmountOnExit>
                      <div>
                        <Dropzone
                          onDrop={handleDrop}
                          accept={{ 'video/mp4': ['.mp4'], 'video/webm': ['.webm'] }}
                          maxFiles={1}
                          maxSize={50 * 1024 * 1024}
                          multiple={false}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div className="dropzone dz-clickable" {...getRootProps()}>
                              <div className="dz-message needsclick">
                                <div className="mb-3">
                                  <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                </div>
                                <h5>فایل ویدیویی را اینجا بکشید یا کلیک کنید (mp4, webm)</h5>
                              </div>
                              <input {...getInputProps()} />
                            </div>
                          )}
                        </Dropzone>
                        {isEditMode && (
                          <Button
                            color="light"
                            size="sm"
                            className="mt-2"
                            onClick={() => {
                              setShowDropzone(false);
                              setSelectedFile(null);
                              validation.setFieldValue('VideoFile', null);
                              validation.setTouched({ ...validation.touched, VideoFile: false });
                              validation.setErrors({ ...validation.errors, VideoFile: undefined });
                            }}
                          >
                            لغو
                          </Button>
                        )}
                      </div>
                    </CSSTransition>

                    {selectedFile && !validation.errors.VideoFile && (
                      <div className="mt-2">
                        <p>
                          <strong>فایل انتخاب‌شده:</strong> {selectedFile.name} (
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                        </p>
                      </div>
                    )}

                    {validation.touched.VideoFile && validation.errors.VideoFile && (
                      <FormFeedback className="d-block">{validation.errors.VideoFile}</FormFeedback>
                    )}
                  </div>

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <Progress animated color="primary" value={uploadProgress} className="mb-3">
                      {uploadProgress}%
                    </Progress>
                  )}

                  <div className="text-end">
                    <Button type="submit" color="primary" disabled={uploadProgress > 0 && uploadProgress < 100}>
                      {isEditMode ? 'بروزرسانی' : 'ثبت'}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default StaticContentForm;
